import { inject, computed, ref } from 'vue'
import { useDiscounts } from './discounts.js'
import { useStorefront } from './storefront.js'
import { useDate } from './date.js'
import { useCapacities } from './capacities.js'
import { useOptions } from './options.js'

export function useCart() {
    const { storefront } = useStorefront()
    const cart = inject('cart', ref([]))
    const products = computed(() => storefront.value?.products ?? [])
    const sorting = computed(() => products.value.map(product => product.id))
    const { date } = useDate()
    const { capacity } = useCapacities(date)
    const { selectedDiscount } = useDiscounts()
    const { options } = useOptions()

    const pax = computed(() => {
        return cart.value.reduce((total, item) => {
            const product = products.value.find(product => product.id === item.id)

            if (!product) {
                return total
            }

            if (product.capacity > 0) {
                return total + ((product?.capacity ?? 0) * item.quantity)
            }

            return total
        }, 0)
    })

    const reduceCartCapacity = (cart) => {
        return Math.max(0, capacity.value - pax.value)
    }

    const computedCart = computed({
        get() {
            return cart.value
                .filter(item => item.total ?? products.value.find(product => product.id === item.id))
                .map(item => {
                    const product = item.total ? null : products.value.find(product => product.id === item.id)
                    const total = product ? (product.price * item.quantity) : item.total
                    const tax = product ? (product.tax * item.quantity) : item.tax

                    return {
                        ...item,
                        total,
                        tax,
                    }
                }).sort((a, b) => sorting.value.indexOf(a.id) - sorting.value.indexOf(b.id))
        },

        set(newCart) {
            cart.value = newCart
        }
    })

    const calculateTotal = (fixedAmount, percentage, maxQty, productPrice, productQty) => {
        if (maxQty === -1) {
            maxQty = productQty
        }


        const subtotal = productPrice * productQty
        const eligibleQty = Math.min(productQty, maxQty)
        const percentageDiscount = ((productPrice * eligibleQty) - fixedAmount) * percentage
        return subtotal - fixedAmount - percentageDiscount
    };

    const totalPreDiscount = computed(() => {
        return computedCart.value
            .map(item => {
                const product = products.value.find(product => product.id === item.id)
                const newItem = {
                    ...item
                }

                if (product && selectedDiscount.value && selectedDiscount.value.products && selectedDiscount.value.products.length) {
                    let fixedDiscount = 0
                    let percentageDiscount = selectedDiscount.value.percentage ?? 1
                    let maxQty = selectedDiscount.value.quantity ?? 1

                    if (selectedDiscount.value && selectedDiscount.value.products.includes(newItem.id) && (selectedDiscount.value.type === 'fixedAmount') || Array.isArray(selectedDiscount.value.type) && selectedDiscount.value.type.includes('fixedAmount')) {
                        fixedDiscount = selectedDiscount.value.value
                    }

                    if (selectedDiscount.value && selectedDiscount.value.products.includes(newItem.id) && (selectedDiscount.value.type === 'amount') || Array.isArray(selectedDiscount.value.type) && selectedDiscount.value.type.includes('amount')) {
                        fixedDiscount = (selectedDiscount.value.value * newItem.quantity)
                    }

                    newItem.total = Math.max(0, calculateTotal(fixedDiscount, percentageDiscount, maxQty, product.price, newItem.quantity))
                }

                return newItem
            })
            .reduce((value, { total }) => value + total, 0)
    })

    const total = computed(() => {
        const computedTotal = totalPreDiscount.value

        if (!computedCart.value.length) {
            return 0
        }

        if (selectedDiscount.value && selectedDiscount.value.type === 'fixedAmount' && (!selectedDiscount.value.products || !selectedDiscount.value.products.length)) {
            const discountMaxAmount = Math.max(0, selectedDiscount.value.value)
            return Math.max(0, computedTotal - discountMaxAmount)
        }

        return computedTotal
    })
    const tax = computed(() => computedCart.value.reduce((value, { tax }) => value + tax, 0))
    const subtotal = computed(() => total.value - tax.value)

    const availableCapacity = computed(() => reduceCartCapacity(computedCart.value))
    const availableCapacityExceptFor = (id) => computed(() => reduceCartCapacity(computedCart.value.filter(item => item.id !== id)))

    return {
        cart: computedCart,
        subtotal,
        tax,
        total,
        totalPreDiscount,
        pax,
        availableCapacity,
        availableCapacityExceptFor,
    }
}